import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import useLocation from '../hooks/useLocation';
import useMediaStream from '../hooks/useMediaStream';
import { Device } from 'mediasoup-client';
import io from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import streamSignals from '../hooks/useStreamSignals';

const StreamContext = createContext();

export const StreamProvider = ({ children }) => {
  const socketUrl = process.env.REACT_APP_STREAM_MEDIASOUP_URL;
  const apiUrl = process.env.REACT_APP_STREAM_API_URL;

  const [streaming, setStreaming] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [userCount, setViewerCount] = useState(0);
  const [streamId, setStreamId] = useState(null);

  const videoRef = useRef(null);
  const { location, fetchLocation } = useLocation();
  const { startPreviewMedia, stopMedia } = useMediaStream(videoRef);

  const socketRef = useRef(null);
  const transportRef = useRef(null);
  const streamRef = useRef(null);

  const registerVideoRef = (ref) => {
    videoRef.current = ref;
  };

  useEffect(() => {
    socketRef.current = io(socketUrl);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
      if (transportRef.current?.close) transportRef.current.close();
      socketRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      const res = await fetch(`${apiUrl}/streams/${streamId}/viewers`);
      const data = await res.json();
      setViewerCount(data.count);
    }, 5000);

    return () => clearInterval(interval);
  }, [streamId]);


  const emitAsync = (event, data = null) =>
    new Promise((resolve, reject) => {
      socketRef.current.emit(event, data, (res) => {
        if (res?.error) return reject(res.error);
        resolve(res);
      });
    });

  const startPreview = async (ref) => {
    if (ref) videoRef.current = ref.current;
    // await fetchLocation();
    await startPreviewMedia();
    setPreviewing(true);
  };

  const startStreaming = async () => {
    try {
      const device = new Device();
      const routerRtpCapabilities = await emitAsync(streamSignals.capabilities);
      await device.load({ routerRtpCapabilities });

      const transportInfo = await emitAsync(streamSignals.producer.create);
      transportRef.current = device.createSendTransport(transportInfo);

      transportRef.current.on('connect', ({ dtlsParameters }, callback, errback) => {
        emitAsync(streamSignals.producer.connect, { dtlsParameters })
          .then(callback)
          .catch(errback);
      });

      transportRef.current.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
        emitAsync(streamSignals.producer.produce, { kind, rtpParameters, appData })
          .then(({ id }) => callback({ id }))
          .catch(errback);
      });

      const streamId = uuidv4();
      setStreamId(streamId);
      await emitAsync(streamSignals.producer.register, { streamId });
      try {
        streamRef.current = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { exact: "environment" } },
            audio: false,
        });
      } catch (err) {
        console.warn('Back camera unavailable, falling back to default camera', err);
        streamRef.current = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
          });
      }

      if (videoRef.current) {
        videoRef.current.current.srcObject = streamRef.current;
        videoRef.current.current.muted = true; // prevent echo
        videoRef.current.current.play().catch((err) => {
          console.warn('Video play failed:', err);
        });
      }

      const sendThumbnail = () => {
        const video = videoRef.current.current; //TODO why is this current current
        if (!video || video.readyState < 2) return;

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const thumbnail = canvas.toDataURL('image/jpeg');
        // console.log(thumbnail);
        socketRef.current.emit(streamSignals.producer.updateThumbnail, { streamId, thumbnail });
      };

      streamRef.current.getTracks().forEach((track) => {
        transportRef.current.produce({ track, appData: { streamId } });
      });

      sendThumbnail();
      setInterval(sendThumbnail, 30000);
      setStreaming(true);
      setPreviewing(false);
    } catch (err) {
      console.error('Streamer error:', err);
    }
  };

  const stopStreaming = async () => {
    // Stop media tracks
    // if (videoRef.current?.srcObject) {
    //   const tracks = videoRef.current.srcObject.getTracks();
    //   tracks.forEach(track => track.stop());
    //   videoRef.current.srcObject = null;
    // }

    stopMedia();
    // Inform the server to remove the stream
    await emitAsync(streamSignals.producer.stop, { streamId },(response) => {
      if (response?.error) {
        console.error('Stop stream error:', response.error);
      } else {
        console.log('Stream stopped successfully');
      }
    });

    // Update UI state
    setStreaming(false);
    setPreviewing(false);
  };

  const handleStart = () => setShowCountdown(true);
  const handleCountdownComplete = () => {
    setShowCountdown(false);
    startStreaming();
  };

  return (
    <StreamContext.Provider
      value={{
        streamId,
        streaming,
        previewing,
        showCountdown,
        userCount,
        startPreview,
        stopStreaming,
        handleStart,
        handleCountdownComplete,
        registerVideoRef
      }}>
      {children}
    </StreamContext.Provider>
  );
};

export const useStream = () => {
  const context = useContext(StreamContext);
  if (!context) throw new Error('useStream must be used within StreamProvider');
  return context;
};